import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { map, mergeMap, switchMap, tap, delay, retryWhen, mapTo, catchError } from 'rxjs/operators';

import * as EventsActions from '../actions/events.actions';
import * as SchedulesActions from '../actions/schedules.actions';
import * as UserActions from '../actions/user.actions';
import * as RoomsActions from '../actions/resources.actions';
import { Observable, forkJoin, of, timer } from 'rxjs';
import { betterLatestFrom, getAttendeeFromLocationAndAttendees, noResponse } from '../utils';
import { Action, Store } from '@ngrx/store';
import { setOperation } from '../actions';
import { Router } from '@angular/router';
import { allResources, getRequirements, selectUser } from '../selectors';
import { loadFavourites } from '../actions/schedules.actions';
import { crateEventError, loadEventsPatched } from '../actions/events.actions';

import { add, set } from 'date-fns';
import { ApiService, createWebexMeeting, eventCreated, eventDeleted, fromLocalized, WebexInvitee } from '@cue/api';
import { CalendarEventDatetime, CalendarsService } from '@cue/calendars';
import { DataService } from '../services/data.service';
import { AssistErrorHandler, AuthService, ConfigService } from '../services';
import { AppState, busyOperation, successOperation } from '../models';
import { TranslocoService } from '@jsverse/transloco';
import { refreshDashboard } from '../actions/dashboard.actions';
import { localized } from '@cue/translate';

@Injectable()
export class EventsEffects {
  private authService = inject(AuthService);
  private calendarsService = inject(CalendarsService);
  private apiService = inject(ApiService);
  private actions$ = inject(Actions);
  private dataService = inject(DataService);
  private configService = inject(ConfigService);
  private store = inject<Store<AppState>>(Store<AppState>);
  private translocoService = inject(TranslocoService);
  private router = inject(Router);

  loadEventsWhenUserHasChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userLoaded, RoomsActions.resourcesLoaded),
      map((_) =>
        EventsActions.loadEventsPatched({
          startFrom: set(new Date(), {
            hours: 0,
            minutes: 0,
          }),
          endFrom: add(
            set(new Date(), {
              hours: 0,
              minutes: 0,
            }),
            { days: 30 }
          ),
        })
      )
    )
  );

  loadEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.loadEventsPatched),
      betterLatestFrom((a) => this.store.select(allResources)),
      switchMap(([action, _allResources]) =>
        this.dataService.getMyEventsWhereIsResource(action.startFrom, action.endFrom, _allResources.data)
      ),
      mergeMap((events) => {
        const mode = this.configService.getMode();
        return [SchedulesActions.refreshSchedules(), EventsActions.eventsLoaded({ events })];
      })
    )
  );

  finishEventById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.finishEventById),
      betterLatestFrom((a) => this.store.select(selectUser)),
      switchMap(([action, user]) =>
        this.calendarsService
          .finishEvent(action.eventToFinishId)
          .pipe(
            mergeMap((resp) => [
              refreshDashboard(),
              loadEventsPatched({
                startFrom: set(new Date(), {
                  hours: 0,
                  minutes: 0,
                }),
                endFrom: add(
                  set(new Date(), {
                    hours: 0,
                    minutes: 0,
                  }),
                  { days: 30 }
                ),
              }),
            ])
          )
          .pipe(delay(this.configService.getMode() === 'touchone-calendar' ? 0 : 3000))
      )
    )
  );

  cancelParticipationEventById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.cancelParticipationEventById),
      switchMap((action) => this.calendarsService.deleteEvent(action.eventToCancelParticipationId)),
      mergeMap((success) =>
        success
          ? [
              loadFavourites(),
              EventsActions.loadEventsPatched({
                startFrom: set(new Date(), {
                  hours: 0,
                  minutes: 0,
                }),
                endFrom: add(
                  set(new Date(), {
                    hours: 0,
                    minutes: 0,
                  }),
                  { days: 30 }
                ),
              }),
              SchedulesActions.refreshSchedules(),
              refreshDashboard(),
            ]
          : []
      )
    )
  );

  deleteEventById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.deleteEventById),
      betterLatestFrom((a) => this.store.select(selectUser)),
      switchMap(([action, user]) => {
        const requests = action.resourceAttendees.map((rA) => {
          return this.apiService.call(
            eventDeleted({
              data: {
                id: action.eventToDeleteId,
                resourceUsername: rA.resource.username,
                iCalUId: action.icalId,
                resourceDisplayName: fromLocalized(
                  rA.resource.displayName,
                  this.translocoService.getActiveLang(),
                  this.translocoService.getDefaultLang()
                ),
                subject: action.subject,
                showAs: action.showAs,
                realEnd: action.end,
                realStart: action.start,
                action: 'delete',
                doneBy: 'assist',
                organiserName: user.data.givenName + ' ' + user.data.surname,
                organiserEmail: this.authService.getEmail(),
              },
              orders: [],
            })
          );
        });
        return forkJoin(requests).pipe(mapTo(action));
      }),
      switchMap((action) =>
        forkJoin([
          this.calendarsService.deleteEvent(action.eventToDeleteId, action.mutationScope),
          of(action.eventToDeleteId),
        ])
      ),
      mergeMap(([success, eid]) =>
        success
          ? [
              loadFavourites(),
              EventsActions.loadEventsPatched({
                startFrom: set(new Date(), {
                  hours: 0,
                  minutes: 0,
                }),
                endFrom: add(
                  set(new Date(), {
                    hours: 0,
                    minutes: 0,
                  }),
                  { days: 30 }
                ),
              }),
              SchedulesActions.refreshSchedules(),
              refreshDashboard(),
            ]
          : [EventsActions.eventDeleteFailed({ eventToDeleteId: eid })]
      )
    )
  );

  tentativePatchedChecking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.eventPatched),
      betterLatestFrom((a) => this.store.select(allResources)),
      mergeMap(([action, resources]) => {
        return this.dataService.getMyEventsWhereIsResource(action.start, action.end, resources.data).pipe(
          map((events) => {
            return events.filter((x) => this.matchesCreatedEvent(x, action.id));
          }),
          switchMap((events) => {
            if (events.length === 0) return of(EventsActions.notFoundEvent());

            const event = events[0];

            const showChangeButton = !event.cancelled && event.end.utcDateTime >= new Date();
            const attendee = getAttendeeFromLocationAndAttendees(
              event,
              resources.data,
              this.translocoService.getActiveLang(),
              this.translocoService.getDefaultLang()
            );
            if (noResponse(attendee.status)) {
              return timer(5000).pipe(
                mapTo(
                  EventsActions.eventPatched({
                    start: action.start,
                    id: action.id,
                    end: action.end,
                  })
                )
              );
            }
            return of(
              EventsActions.eventResponded({
                id: action.id,
                attendee: attendee,
                showChangeButton: showChangeButton,
              })
            );
          })
        );
      })
    )
  );

  tentativecChecking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EventsActions.eventCreated),
      betterLatestFrom((a) => this.store.select(allResources)),
      mergeMap(([action, resources]) => {
        return this.dataService.getMyEventsWhereIsResource(action.start, action.end, resources.data).pipe(
          map((events) => {
            return events.filter((x) => this.matchesCreatedEvent(x, action.id, action.iCalUId));
          }),
          switchMap((events) => {
            if (events.length === 0) return of(EventsActions.notFoundEvent());

            const event = events[0];

            const showChangeButton = !event.cancelled && event.end.utcDateTime >= new Date();
            const attendee = getAttendeeFromLocationAndAttendees(
              event,
              resources.data,
              this.translocoService.getActiveLang(),
              this.translocoService.getDefaultLang()
            );
            if (noResponse(attendee.status)) {
              return timer(5000).pipe(
                mapTo(
                  EventsActions.eventCreated({
                    start: action.start,
                    id: action.id,
                    iCalUId: action.iCalUId,
                    end: action.end,
                    subject: action.subject,
                    suppressCreatedNotification: action.suppressCreatedNotification,
                  })
                )
              );
            }
            return of(
              EventsActions.eventResponded({
                id: action.id,
                attendee: attendee,
                showChangeButton: showChangeButton,
              })
            );
          })
        );
      })
    )
  );

  createEvent$ = createEffect(
    (): Observable<Action> =>
      this.actions$.pipe(
        ofType(EventsActions.createEvent),
        tap((_) =>
          this.store.dispatch(
            setOperation({
              operation: busyOperation('reservation', 'todo-predelat-na-uuid'),
            })
          )
        ),
        switchMap((action) => {
          const locations = action.resourceAttendees.map((resourceAttendee) => ({
            roomName: localized(resourceAttendee.resource.displayName, this.translocoService),
            roomEmail: resourceAttendee.resource.email,
            roomUsername: resourceAttendee.resource.username,
          }));
          const resourceEmails = new Set(action.resourceAttendees.map((rA) => rA.resource.email.toLowerCase()));
          const calendarAttendees = [
            ...action.resourceAttendees.map((resourceAttendee) => ({
              isResource: true,
              address: resourceAttendee.resource.email,
              name: resourceAttendee.resource.name,
              type: 'required' as const,
            })),
            ...action.attendees.filter(
              (attendee) => !attendee.isResource && !resourceEmails.has(attendee.address.toLowerCase())
            ),
          ];
          let webexMeetingUrl: string | undefined;
          const eventWithAction$ = action.webexMeeting
            ? this.apiService
                .call(
                  createWebexMeeting({
                    eventStart: action.start,
                    eventEnd: action.end,
                    eventTitle: action.subject,
                    eventBody: action.text,
                    invitees: action.attendees.map(
                      (attendee) =>
                        ({
                          email: attendee.address,
                          coHost: false,
                          displayName: attendee.name,
                        }) as WebexInvitee
                    ),
                  })
                )
                .pipe(
                  catchError(() =>
                    of({
                      data: null,
                      success: false,
                    })
                  ),
                  tap((response) => {
                    webexMeetingUrl = response.success ? response.data?.weblink : undefined;
                    if (response.data == null || !response.success) {
                      this.assistErrorHandler.handleError({
                        stack: undefined,
                        message: 'Cannot create webex meetings',
                      });
                    }
                  }),
                  switchMap((response) =>
                    forkJoin([
                      of(action),
                      this.calendarsService.createEvent(
                        action.visibility,
                        action.showAs,
                        action.subject,
                        action.text,
                        CalendarEventDatetime.fromBrowserDatetime(action.start),
                        CalendarEventDatetime.fromBrowserDatetime(action.end),
                        locations,
                        calendarAttendees,
                        action.onlineMeeting,
                        response.data && response.success
                          ? {
                              url: response.data.weblink,
                              code: response.data.meetingNumber,
                              password: response.data.password,
                              body: action.webexMeeting.body,
                            }
                          : null,
                        action.reminder,
                        action.recurrence,
                        { isAllDay: action.isAllDay === true }
                      ),
                    ])
                  )
                )
            : forkJoin([
                of(action),
                this.calendarsService.createEvent(
                  action.visibility,
                  action.showAs,
                  action.subject,
                  action.text,
                  CalendarEventDatetime.fromBrowserDatetime(action.start),
                  CalendarEventDatetime.fromBrowserDatetime(action.end),
                  locations,
                  calendarAttendees,
                  action.onlineMeeting,
                  null,
                  action.reminder,
                  action.recurrence,
                  { isAllDay: action.isAllDay === true }
                ),
              ]);

          return eventWithAction$.pipe(
            betterLatestFrom(() => this.store.select(allResources)),
            betterLatestFrom(() => this.store.select(getRequirements)),
            betterLatestFrom(() => this.store.select(selectUser)),
            switchMap(([[[[action, response], _allResources], requirements], user]) =>
              forkJoin([
                this.dataService
                  .getMyEventsWhereIsResource(
                    add(set(action.start, { seconds: 0, milliseconds: 0 }), {
                      minutes: -10,
                    }),
                    add(set(action.end, { seconds: 0, milliseconds: 0 }), {
                      minutes: 10,
                    }),
                    _allResources.data
                  )
                  .pipe(
                    map((events) => {
                      if (events.find((x) => this.matchesCreatedEvent(x, response.id, response.iCalId)) == null)
                        throw new Error('NO Events created yet');
                      return response;
                    }),
                    retryWhen((errors) => errors.pipe(delay(2000)))
                  ),
                of(requirements),
                of(action),
                ...action.resourceAttendees.map((rA) =>
                  this.apiService
                    .call(
                      eventCreated({
                        data: {
                          doneBy: 'assist',
                          onlineMeetingUrl: webexMeetingUrl,
                          onlineMeetingAttendeeEmails: webexMeetingUrl
                            ? action.attendees
                                .filter((attendee) => !attendee.isResource)
                                .map((attendee) => attendee.address)
                            : undefined,
                          id: response.id,
                          resourceDisplayName: localized(rA.resource.displayName, this.translocoService),
                          resourceUsername: rA.resource.username,
                          iCalUId: response.iCalId,
                          subject: action.subject,
                          showAs: action.showAs.value,
                          realEnd: action.end,
                          realStart: action.start,
                          organiserName: user.data.givenName + ' ' + user.data.surname,
                          organiserEmail: this.authService.getEmail(),
                          action: 'create',
                        },
                        orders: action.orders.filter((x) => x.resourceIdForFiltering === rA.resource.idForFiltering),
                      })
                    )
                    .pipe(
                      tap((result) => {
                        if (webexMeetingUrl && !result.success) {
                          throw new Error('Cannot save the Webex meeting link');
                        }
                      }),
                      delay(this.configService.getMode() === 'touchone-calendar' ? 0 : 3000)
                    )
                ),
              ])
            ),
            tap(([, , , _]) => {
              if (!action.skipReservationNavigation && this.router.routerState.snapshot.url.includes('reservations')) {
                this.router.navigate(['/reservations', 'reserve']);
              }
            }),
            mergeMap((a): Action[] => [
              loadFavourites(),
              refreshDashboard(),
              SchedulesActions.refreshSchedules(),
              EventsActions.loadEventsPatched({
                startFrom: set(new Date(), {
                  hours: 0,
                  minutes: 0,
                }),
                endFrom: add(
                  set(new Date(), {
                    hours: 0,
                    minutes: 0,
                  }),
                  { days: 30 }
                ),
              }),
              EventsActions.eventCreated({
                id: a[0].id,
                iCalUId: a[0].iCalId,
                start: a[2].start,
                roomEmails: a[2].resourceAttendees.map((rA) => rA.resource.email),
                subject: a[2].subject,
                skipReservationNavigation: a[2].skipReservationNavigation,
                suppressCreatedNotification: a[2].suppressCreatedNotification,
                end: a[2].end,
              }),
              setOperation({
                operation: successOperation('reservation', null, 'todo-predelat-na-uuid'),
              }),
            ]),
            catchError((error): Observable<Action> => {
              return of(
                crateEventError({
                  error: (error as any)?.error?.error,
                })
              );
            })
          );
        })
      ) as Observable<Action>,
    {
      useEffectsErrorHandler: true,
    }
  );

  private assistErrorHandler: AssistErrorHandler;

  constructor() {
    const erroHandler = inject(ErrorHandler);

    this.assistErrorHandler = erroHandler as AssistErrorHandler;
  }

  private matchesCreatedEvent(
    event: { id?: string; iCalUId?: string; seriesMasterId?: string },
    id?: string,
    iCalUId?: string
  ): boolean {
    if (!event) {
      return false;
    }

    if (id && (event.id === id || event.seriesMasterId === id)) {
      return true;
    }

    if (iCalUId && event.iCalUId === iCalUId) {
      return true;
    }

    return false;
  }
}
